import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import useTableController from "../../../components/table/useTableController";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";
import {
  listReservations,
  createReservation,
  cancelReservation,
  listVenuesBasic,
  getReservationsStats,
} from "./reservationsApi";
import {
  getReservationRangeParam,
  hasReservationGlobalSearch,
} from "./reservationListSearch";
import ReservationFormAdmin from "./ReservationFormAdmin";
import ReusableModal from "../../../components/common/ReusableModal";
import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormTextarea from "../../../components/form/FormTextarea";
import {
  Plus,
  Trash2,
  CalendarDays,
  Repeat,
  Eye,
  Archive,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

import "./reservation.css";

const ReservationsList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [raw, setRaw] = useState([]);
  const [total, setTotal] = useState(0);

  const [stats, setStats] = useState({
    today: 0,
    tomorrow: 0,
    thisWeek: 0,
    newToday: 0,
    totalBookings: 0,
    userTotalBookings: 0,
    adminTotalBookings: 0,
    userTodayBookings: 0,
    adminTodayBookings: 0,
    userTomorrowBookings: 0,
    adminTomorrowBookings: 0,
    userThisWeekBookings: 0,
    adminThisWeekBookings: 0,
    userNewTodayBookings: 0,
    adminNewTodayBookings: 0,
  });

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [filters, setFilters] = useState({
    status: "all",
    venue: "all",
    range: "today",
  });

  const [venueOptions, setVenueOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("user");
  const [sort, setSort] = useState({ sortBy: "when", sortDir: "asc" });

  const basePath = location.pathname.startsWith("/venue-admin")
    ? "/venue-admin"
    : location.pathname.startsWith("/country-admin")
    ? "/country-admin"
    : "/super-admin";

  const [vaVenueId, setVaVenueId] = useState(null);
  const [vaRole, setVaRole] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);
  const [expandedSlots, setExpandedSlots] = useState({});

  const tableWrapRef = useRef(null);
  const axisLockRef = useRef({ startX: 0, startY: 0, locked: null });
  const loadRequestIdRef = useRef(0);

  useEffect(() => {
    try {
      const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");

      if (ctx.role === "venue_admin") {
        setVaRole("venue_admin");
        setVaVenueId(ctx.venueId || null);
      }
    } catch (_) {}
  }, []);

  const fmtTime12 = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return "—";

    const [hStr, mStr] = hhmm.split(":");
    const h = Number(hStr);
    const m = Number(mStr);

    if (!Number.isFinite(h) || !Number.isFinite(m)) return "—";

    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const min2 = String(m).padStart(2, "0");

    return `${hour12}:${min2} ${ampm}`;
  };

  const fmtSlotRange12 = (slot) => {
    if (!slot?.start || !slot?.end) return "—";

    const a = fmtTime12(slot.start);
    const b = fmtTime12(slot.end);

    if (a === "—" || b === "—") return "—";

    return `${a} - ${b}`;
  };

  const fmtBookedOn = (d) => {
    if (!d) return "—";

    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";

    return dt
      .toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  };

  const fmtWhenDate = (d) => {
    if (!d) return "—";

    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";

    return dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const tableItems = useMemo(() => {
    return (raw || []).map((r) => {
      const occurrences = (Array.isArray(r?.reservationDates)
        ? r.reservationDates
        : []
      ).flatMap((day) => {
        const date = day?.date ? new Date(day.date) : null;

        if (!date || Number.isNaN(date.getTime())) {
          return [];
        }

        const dateDisplay = fmtWhenDate(date);
        const daySlots = Array.isArray(day?.timeSlots) ? day.timeSlots : [];

        return daySlots.map((slot) => ({
          ...slot,
          dateDisplay,
          slotDisplay: fmtSlotRange12(slot),
        }));
      });

      const primaryOccurrence = occurrences[0] || null;
      const primaryDateStr = primaryOccurrence?.dateDisplay || "—";
      const primarySlotStr = primaryOccurrence?.slotDisplay || "—";

      const extraSlots = occurrences.slice(1).map((occurrence) => ({
        ...occurrence,
        display:
          occurrence.dateDisplay === primaryDateStr
            ? occurrence.slotDisplay
            : `${occurrence.dateDisplay} • ${occurrence.slotDisplay}`,
      }));

      const scheduleGroups = occurrences.reduce((groups, occurrence) => {
        const existing = groups.find(
          (group) => group.dateDisplay === occurrence.dateDisplay,
        );

        if (existing) {
          existing.slots.push(occurrence.slotDisplay);
          return groups;
        }

        groups.push({
          dateDisplay: occurrence.dateDisplay,
          slots: [occurrence.slotDisplay],
        });

        return groups;
      }, []);

      const extraSlotsCount = extraSlots.length;
      const whenStr = occurrences.length
        ? `${occurrences
            .map((occurrence) =>
              `${occurrence.dateDisplay} ${occurrence.slotDisplay}`.trim(),
            )
            .join(" ")}${extraSlotsCount > 0 ? ` (+${extraSlotsCount} more)` : ""}`
        : "—";

      return {
        ...r,
        payer: r.userName || r?.userId?.fullName || "—",
        phone: r.contactNumber || r?.userId?.mobileNumber || "—",
        bookingId: r.generatedReservationId || r._id || "—",
        status: r.reservationStatus || "pending",
        paymentState: r.paymentStatus || r.payment_status || "pending",
        archiveReason: String(r.cancellationReason || "").trim() || "—",
        amount: Number(r.totalAmount || 0),
        bookedOnStr: fmtBookedOn(r?.createdAt),
        whenStr,
        whenDateStr: primaryDateStr,
        primarySlotStr,
        scheduleGroups,
        extraSlots,
        extraSlotsCount,
      };
    });
  }, [raw]);

  const tc = useTableController(tableItems, {
    // /api/reservations performs the global search before server pagination.
    // Do not re-filter only the current page in useTableController.
    searchKeys: [],
    initialPageSize: 10,
    pagination: { mode: "server", total },
  });

  const [serverSearchTerm, setServerSearchTerm] = useState("");
  const isGlobalSearch = hasReservationGlobalSearch(tc.searchTerm);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setServerSearchTerm(tc.searchTerm.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [tc.searchTerm]);

  const showingFrom = total === 0 ? 0 : tc.startIndex + 1;
  const showingTo = Math.min(tc.startIndex + tc.rows.length, total);

  const statusTone = (status = "pending") => {
    switch (status) {
      case "confirmed":
        return { tone: "success", label: "Confirmed" };
      case "cancelled":
        return { tone: "danger", label: "Cancelled" };
      case "expired":
        return { tone: "muted", label: "Expired" };
      default:
        return { tone: "warn", label: "Pending" };
    }
  };

  const paymentStatusTone = (status = "pending") => {
    switch (String(status || "").toLowerCase()) {
      case "completed":
      case "paid":
        return { tone: "success", label: "Paid" };
      case "failed":
        return { tone: "danger", label: "Failed" };
      case "expired":
        return { tone: "muted", label: "Expired" };
      default:
        return { tone: "warn", label: "Pending" };
    }
  };

  const selectTab = (nextTab) => {
    setActiveTab(nextTab);
    setFilters((prev) => ({
      ...prev,
      status: "all",
    }));
  };

  const loadVenuesForFilter = async () => {
    if (vaRole === "venue_admin") return;

    try {
      const items = await listVenuesBasic({ page: 1, pageSize: 200, q: "" });
      const opts = (items || []).map((v) => ({ value: v._id, label: v.name }));
      setVenueOptions(opts);
    } catch (e) {
      console.error("[ReservationsList] load venues failed", e);
      setVenueOptions([]);
    }
  };

  useEffect(() => {
    loadVenuesForFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaRole]);

  const load = async () => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setLoading(true);

    try {
      const isArchive = activeTab === "archive";
      const tabFilter = isArchive
        ? {}
        : activeTab === "admin"
          ? { isAdminReservation: true }
          : { isUserReservation: true };

      const resolvedVenue =
        vaRole === "venue_admin"
          ? vaVenueId
          : filters.venue && filters.venue !== "all"
          ? filters.venue
          : undefined;

      const apiParams = {
        ...tabFilter,
        lifecycle: isArchive ? "archive" : "active",
        status: filters.status !== "all" ? filters.status : undefined,
        venue: resolvedVenue,
        range: getReservationRangeParam({
          searchTerm: serverSearchTerm,
          range: filters.range,
          isArchive,
        }),
        page: tc.page,
        pageSize: tc.pageSize,
        q: serverSearchTerm || undefined,
        sortBy: sort.sortBy,
        sortDir: sort.sortDir,
      };

      let data;

      if (isArchive) {
        data = await listReservations(apiParams);
      } else {
        const statsParams = {
          venue: resolvedVenue,
          status: filters.status !== "all" ? filters.status : undefined,
        };
        const [listData, s] = await Promise.all([
          listReservations(apiParams),
          getReservationsStats(statsParams),
        ]);

        if (requestId !== loadRequestIdRef.current) return;

        data = listData;
        setStats(s || {});
      }

      if (requestId !== loadRequestIdRef.current) return;

      setRaw(Array.isArray(data?.items) ? data.items : []);
      setExpandedSlots({});
      setTotal(Number(data?.total || 0));
    } catch (e) {
      if (requestId === loadRequestIdRef.current) {
        toast.error(e?.response?.data?.message || "Failed to load reservations");
      }
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (tc.searchTerm.trim() !== serverSearchTerm) return;

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    filters.status,
    filters.venue,
    filters.range,
    tc.page,
    tc.pageSize,
    serverSearchTerm,
    vaVenueId,
    vaRole,
    sort.sortBy,
    sort.sortDir,
  ]);

  useEffect(() => {
    tc.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.status,
    filters.venue,
    filters.range,
    activeTab,
    vaVenueId,
    vaRole,
    sort.sortBy,
    sort.sortDir,
  ]);

  const filterDefs = useMemo(
    () => [
      {
        id: "status",
        label: "Status",
        options:
          activeTab === "archive"
            ? [
                { value: "all", label: "All" },
                { value: "cancelled", label: "Cancelled" },
                { value: "expired", label: "Expired" },
              ]
            : [
                { value: "all", label: "All" },
                { value: "pending", label: "Pending" },
                { value: "confirmed", label: "Confirmed" },
              ],
      },
      ...(vaRole === "venue_admin"
        ? []
        : [
            {
              id: "venue",
              label: "Venue",
              options: [{ value: "all", label: "All venues" }, ...venueOptions],
            },
          ]),
    ],
    [activeTab, venueOptions, vaRole],
  );

  const handleCreate = async (payload) => {
    try {
      await createReservation(payload);
      toast.success("Reservation created");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Create failed");
      throw e;
    }
  };

  const handleCancel = (row) => {
    const id = row?._id || row?.id;
    if (!id) return;

    setCancelTarget(row);
    setCancelReason("");
  };

  const performCancel = async () => {
    if (!cancelTarget) return;

    if (!cancelReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }

    const id = cancelTarget._id || cancelTarget.id;

    setCancelSaving(true);
    setBusyId(id);

    try {
      await cancelReservation(id, { reason: cancelReason.trim() });
      toast.success("Reservation cancelled");
      setCancelTarget(null);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Cancel failed");
    } finally {
      setCancelSaving(false);
      setBusyId(null);
    }
  };

  const tabCounts = useMemo(() => {
    const r = isGlobalSearch ? "all" : filters.range;

    if (r === "today") {
      return {
        user: stats.userTodayBookings ?? stats.userTotalBookings,
        admin: stats.adminTodayBookings ?? stats.adminTotalBookings,
      };
    }

    if (r === "tomorrow") {
      return {
        user: stats.userTomorrowBookings ?? stats.userTotalBookings,
        admin: stats.adminTomorrowBookings ?? stats.adminTotalBookings,
      };
    }

    if (r === "this_week") {
      return {
        user: stats.userThisWeekBookings ?? stats.userTotalBookings,
        admin: stats.adminThisWeekBookings ?? stats.adminTotalBookings,
      };
    }

    if (r === "new_today") {
      return {
        user: stats.userNewTodayBookings ?? stats.userTotalBookings,
        admin: stats.adminNewTodayBookings ?? stats.adminTotalBookings,
      };
    }

    return {
      user: stats.userTotalBookings,
      admin: stats.adminTotalBookings,
    };
  }, [filters.range, isGlobalSearch, stats]);

  const toggleSort = (col) => {
    setSort((prev) => {
      if (prev.sortBy !== col) {
        return col === "booked_on"
          ? { sortBy: "booked_on", sortDir: "desc" }
          : { sortBy: "when", sortDir: "asc" };
      }

      return {
        sortBy: prev.sortBy,
        sortDir: prev.sortDir === "asc" ? "desc" : "asc",
      };
    });
  };

  const toggleSlotsRow = (id) => {
    setExpandedSlots((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const collapseSlotsRow = (id) => {
    setExpandedSlots((prev) => {
      if (!prev[id]) return prev;

      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;

      axisLockRef.current.startX = t.clientX;
      axisLockRef.current.startY = t.clientY;
      axisLockRef.current.locked = null;
    };

    const onTouchMove = (e) => {
      const t = e.touches?.[0];
      if (!t) return;

      const canScrollX = el.scrollWidth > el.clientWidth + 1;
      if (!canScrollX) return;

      const dx = t.clientX - axisLockRef.current.startX;
      const dy = t.clientY - axisLockRef.current.startY;

      const ax = Math.abs(dx);
      const ay = Math.abs(dy);

      const TH = 12;
      const RATIO = 1.35;

      if (!axisLockRef.current.locked) {
        if (ax > TH && ax > ay * RATIO) axisLockRef.current.locked = "x";
        else if (ay > TH && ay > ax * RATIO) axisLockRef.current.locked = "y";
        else return;
      }

      if (axisLockRef.current.locked === "x") {
        const atLeftEdge = el.scrollLeft <= 0 && dx > 0;
        const atRightEdge =
          el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 && dx < 0;

        if (!atLeftEdge && !atRightEdge) {
          e.preventDefault();
        }
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, []);

  const SortIcon = ({ col }) => {
    const active = sort.sortBy === col;

    if (!active) {
      return (
        <span className="rl-sort-icon is-muted">
          <ChevronUp size={14} />
        </span>
      );
    }

    return sort.sortDir === "asc" ? (
      <span className="rl-sort-icon">
        <ChevronUp size={14} />
      </span>
    ) : (
      <span className="rl-sort-icon">
        <ChevronDown size={14} />
      </span>
    );
  };

  return (
    <div className="rl-page">
      <div className="sa-head-row rl-head-row">
        <h1 className="sa-h1">
          {activeTab === "archive"
            ? "Expired Bookings"
            : activeTab === "admin"
              ? "Admin Bookings"
              : "User Bookings"}
        </h1>

        {activeTab !== "archive" && (
          <div className="rl-head-actions">
          <button
            type="button"
            className="rl-pill"
            title="Upcoming bookings (grouped)"
            onClick={() => navigate(`${basePath}/reservations/upcoming`)}
          >
            <CalendarDays className="lucide" />
            <span>Upcoming</span>
          </button>

          <button
            type="button"
            className="rl-pill"
            title="Create / manage recurring holds"
            onClick={() => navigate(`${basePath}/reservations/recurring`)}
          >
            <Repeat className="lucide" />
            <span>Recurring Hold</span>
          </button>

          <FormButton
            type="button"
            variant="primary"
            className="rl-create-btn"
            onClick={() => setCreating(true)}
          >
            <Plus className="lucide" /> Create
          </FormButton>
          </div>
        )}
      </div>

      <div className="sa-toolbar-wrap rl-toolbar-wrap">
        <TableToolbar
          searchTerm={tc.searchTerm}
          onSearchChange={tc.setSearch}
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(id, val) => setFilters((p) => ({ ...p, [id]: val }))}
          searchPlaceholder={
            activeTab === "archive"
              ? "Search expired bookings…"
              : activeTab === "admin"
                ? "Search admin bookings…"
                : "Search user bookings…"
          }
        />
      </div>

      {activeTab !== "archive" && (
        <div className="rl-kpis">
        <button
          type="button"
          className={`sa-card rl-kpi ${!isGlobalSearch && filters.range === "today" ? "is-active-green" : ""}`}
          onClick={() =>
            setFilters((p) => ({
              ...p,
              range: p.range === "today" ? "all" : "today",
            }))
          }
          title={
            !isGlobalSearch && filters.range === "today"
              ? "Showing today's bookings (click to clear)"
              : "Show today's bookings"
          }
        >
          <div className="rl-kpi-head">
            <div className="rl-kpi-label green">TODAY&apos;S GAMES</div>
            <CalendarDays className="lucide" />
          </div>

          <div className="rl-kpi-value">{stats.today}</div>
        </button>

        <button
          type="button"
          className={`sa-card rl-kpi ${!isGlobalSearch && filters.range === "tomorrow" ? "is-active-blue" : ""}`}
          onClick={() =>
            setFilters((p) => ({
              ...p,
              range: p.range === "tomorrow" ? "all" : "tomorrow",
            }))
          }
        >
          <div className="rl-kpi-head">
            <div className="rl-kpi-label blue">TOMORROW&apos;S GAMES</div>
            <CalendarDays className="lucide" />
          </div>

          <div className="rl-kpi-value">{stats.tomorrow}</div>
        </button>

        <button
          type="button"
          className={`sa-card rl-kpi ${!isGlobalSearch && filters.range === "this_week" ? "is-active-yellow" : ""}`}
          onClick={() =>
            setFilters((p) => ({
              ...p,
              range: p.range === "this_week" ? "all" : "this_week",
            }))
          }
        >
          <div className="rl-kpi-label">THIS WEEK</div>
          <div className="rl-kpi-value">{stats.thisWeek}</div>
        </button>

        <button
          type="button"
          className={`sa-card rl-kpi ${!isGlobalSearch && filters.range === "new_today" ? "is-active-purple" : ""}`}
          onClick={() =>
            setFilters((p) => ({
              ...p,
              range: p.range === "new_today" ? "all" : "new_today",
            }))
          }
        >
          <div className="rl-kpi-label">NEW TODAY</div>
          <div className="rl-kpi-value">{stats.newToday}</div>
        </button>

        <button
          type="button"
          className={`sa-card rl-kpi ${!isGlobalSearch && filters.range === "all" ? "is-active-muted" : ""}`}
          onClick={() =>
            setFilters((p) => ({
              ...p,
              range: p.range === "all" ? "today" : "all",
            }))
          }
        >
          <div className="rl-kpi-label muted">TOTAL BOOKINGS</div>
          <div className="rl-kpi-value">{stats.totalBookings}</div>
        </button>
        </div>
      )}

      <div className="rl-booking-nav" aria-label="Booking views">
        <div className="rl-booking-tabs" role="tablist" aria-label="Active booking type">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "user"}
            className={`rl-booking-tab ${activeTab === "user" ? "is-active" : ""}`}
            onClick={() => selectTab("user")}
          >
            <span>User Bookings</span>
            <span className="rl-booking-tab-count">{tabCounts.user}</span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "admin"}
            className={`rl-booking-tab ${activeTab === "admin" ? "is-active" : ""}`}
            onClick={() => selectTab("admin")}
          >
            <span>Admin Bookings</span>
            <span className="rl-booking-tab-count">{tabCounts.admin}</span>
          </button>
        </div>

        <button
          type="button"
          className={`rl-archive-tab ${activeTab === "archive" ? "is-active" : ""}`}
          onClick={() => selectTab("archive")}
          aria-pressed={activeTab === "archive"}
          title="View expired and cancelled booking history"
        >
          <Archive className="lucide" aria-hidden="true" />
          <span>Expired Bookings</span>
        </button>
      </div>

      <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder rl-loading">Loading…</div>
        ) : tc.rows.length === 0 ? (
          <div className="sa-empty">
            <div>
              {activeTab === "archive"
                ? "No expired bookings found."
                : "No reservations found."}
            </div>

            {activeTab !== "archive" && (
              <FormButton
                type="button"
                variant="primary"
                onClick={() => setCreating(true)}
              >
                Create reservation
              </FormButton>
            )}
          </div>
        ) : (
          <>
            <div className="sa-table-wrap rl-table-wrap" ref={tableWrapRef}>
              <table className="sa-table rl-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>S.N.</th>
                    <th>Booking ID</th>
                    <th>Player</th>
                    <th>Phone</th>
                    <th>Venue / Ground</th>

                    <th
                      onClick={() => toggleSort("booked_on")}
                      className="rl-sortable-th"
                    >
                      <span>Booked On</span>
                      <SortIcon col="booked_on" />
                    </th>

                    <th onClick={() => toggleSort("when")} className="rl-sortable-th">
                      <span>When</span>
                      <SortIcon col="when" />
                    </th>

                    <th>Amount</th>
                    <th>Status</th>
                    {activeTab === "archive" && <th>Payment</th>}
                    {activeTab === "archive" && <th>Reason</th>}
                    <th style={{ textAlign: "right", width: 120 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tc.rows.map((r, index) => {
                    const id = r._id || r.id;
                    const isExpanded = !!expandedSlots[id];

                    return (
                      <tr key={id || r.bookingId}>
                        <td>{(tc.page - 1) * tc.pageSize + index + 1}</td>
                        <td>{r.bookingId}</td>
                        <td>{r.payer}</td>
                        <td>{r.phone}</td>

                        <td>
                          <div>{r.venueName || "—"}</div>
                          <small className="rl-muted">{r.groundName || "—"}</small>
                        </td>

                        <td>{r.bookedOnStr}</td>

                        <td>
                          <div className="rl-schedule-cell">
                            {isExpanded ? (
                              <div className="rl-schedule-groups">
                                {r.scheduleGroups.map((group, groupIndex) => (
                                  <div
                                    key={`${id}-${group.dateDisplay}-${groupIndex}`}
                                    className="rl-schedule-group"
                                  >
                                    <div className="rl-schedule-date">
                                      {group.dateDisplay}
                                    </div>

                                    <div className="rl-schedule-slots">
                                      {group.slots.map((slot, slotIndex) => (
                                        <span
                                          key={`${id}-${groupIndex}-${slotIndex}`}
                                          className="rl-schedule-slot"
                                        >
                                          {slot}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <>
                                <div className="rl-schedule-date">
                                  {r.whenDateStr}
                                </div>
                                <div className="rl-schedule-slots">
                                  <span className="rl-schedule-slot">
                                    {r.primarySlotStr}
                                  </span>
                                </div>
                              </>
                            )}

                            {r.extraSlotsCount > 0 && (
                              <button
                                type="button"
                                className="rl-more-slots"
                                aria-expanded={isExpanded}
                                onClick={() =>
                                  isExpanded
                                    ? collapseSlotsRow(id)
                                    : toggleSlotsRow(id)
                                }
                              >
                                {isExpanded
                                  ? "Hide slots"
                                  : `View all ${r.extraSlotsCount + 1} slots`}
                              </button>
                            )}
                          </div>
                        </td>

                        <td>₹{r.amount.toLocaleString("en-IN")}</td>

                        <td>
                          {(() => {
                            const { tone, label } = statusTone(r.status);

                            return (
                              <span className={`sa-chip sa-chip--${tone}`}>
                                {label}
                              </span>
                            );
                          })()}
                        </td>

                        {activeTab === "archive" && (
                          <td>
                            {(() => {
                              const { tone, label } = paymentStatusTone(
                                r.paymentState,
                              );

                              return (
                                <span className={`sa-chip sa-chip--${tone}`}>
                                  {label}
                                </span>
                              );
                            })()}
                          </td>
                        )}

                        {activeTab === "archive" && (
                          <td>
                            <span
                              className="rl-muted"
                              title={
                                r.archiveReason !== "—"
                                  ? r.archiveReason
                                  : undefined
                              }
                            >
                              {r.archiveReason}
                            </span>
                          </td>
                        )}

                        <td className="rl-actions">
                          <div className="rl-actions-wrap">
                            {activeTab !== "archive" && (
                              <button
                                type="button"
                                className="sa-icon-link danger"
                                title="Cancel reservation"
                                aria-label="Cancel reservation"
                                onClick={() => handleCancel(r)}
                                disabled={
                                  busyId === id ||
                                  r.status === "cancelled" ||
                                  r.status === "expired"
                                }
                              >
                                <Trash2 className="lucide" />
                              </button>
                            )}

                            <button
                              type="button"
                              className="sa-icon-link"
                              title="View details"
                              aria-label="View details"
                              onClick={() => navigate(`${basePath}/reservations/${id}`)}
                            >
                              <Eye className="lucide" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />

            <div className="rl-results-info">
              Showing {showingFrom} to {showingTo} of {total} results
            </div>
          </>
        )}
      </div>

      {creating && activeTab !== "archive" && (
        <ReservationFormAdmin
          open={creating}
          mode="create"
          isAdmin={activeTab === "admin"}
          isVenueAdmin={vaRole === "venue_admin"}
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      <ReusableModal
        open={!!cancelTarget}
        title="Cancel Booking"
        width="560px"
        onClose={() => {
          if (!cancelSaving) setCancelTarget(null);
        }}
        closeOnBackdrop={!cancelSaving}
      >
        <div className="rl-cancel-wrap">
          <div className="sa-warn">
            This will cancel the booking and notify the user with your reason.
          </div>

          <FormField label="Reason for cancellation">
            <FormTextarea
              rows={3}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder="e.g. Ground maintenance / double booking / weather issues"
              disabled={cancelSaving}
            />
          </FormField>

          <div className="promo-actions rl-cancel-actions">
            <FormButton
              type="button"
              variant="secondary"
              onClick={() => setCancelTarget(null)}
              disabled={cancelSaving}
            >
              Keep Booking
            </FormButton>

            <FormButton
              type="button"
              variant="danger"
              onClick={performCancel}
              disabled={cancelSaving}
            >
              {cancelSaving ? "Cancelling..." : "Confirm Cancel"}
            </FormButton>
          </div>
        </div>
      </ReusableModal>
    </div>
  );
};

export default ReservationsList;